/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import ViewMode from "App/view/modes/view_mode";
import RenderDocument from "App/render_document";
import {vec2, vec3, vec4} from "gl-matrix";
import Light from "App/view/lights/light";
import {logInfo} from "App/debug";
import SunLight from "App/view/lights/sun_light";
import HeadLight from "App/view/lights/head_light";
import Camera from "App/view/cameras/camera";
import BoundingBox from "App/utils/bounding_box";
import Messenger from "App/messenger";
import {project, unProject} from "App/utils/glu";
import Hud from "App/view/hud";
import ViewControls from "App/view/controls/view_controls";
import ExaminerControls from "App/view/controls/examiner_controls";
import ExaminerControlsStereo from "App/view/controls/examiner_controls_stereo";
import ExaminerControlsWebVR from "App/view/controls/examiner_controls_vr";
import FlyControls from "App/view/controls/fly_controls";
import FlyControlsStereo from "App/view/controls/fly_controls_stereo";
import WalkControls from "App/view/controls/walk_controls";
import WalkControlsStereo from "App/view/controls/walk_controls_stereo";
import WalkControlsWebVR from "App/view/controls/walk_controls_vr";
import FlyControlsWebVR from "App/view/controls/fly_controls_vr";
import YAnimationControls from "App/view/controls/y_animation_controls";
import FileAnimationControls from "App/view/controls/file_animation_controls";
import FileAnimationControlsStereo from "App/view/controls/file_animation_controls_stereo";
import FileAnimationControlsWebVR from "App/view/controls/file_animation_controls_web_vr";
import CameraWebVR from "App/view/cameras/camera_web_vr";
import ViewModeWebVR from "App/view/modes/view_mode_web_vr";
import Canvas from "App/canvas";
import {EViewControls} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

/**
 * @param aViewMode
 * @param aDocument
 * @param aPositionPct
 * @param aSizePct
 * @change onSomething when returns true, don't send signal the to next observer
 * @constructor
 */
export default
class View {

    protected  document: RenderDocument;
    protected _viewMode: ViewMode | ViewModeWebVR;
    protected _controls: ViewControls;
    protected viewPositionPct: vec2;
    protected viewSizePct: vec2;
    protected position: vec2 = vec2.fromValues(0, 0); // try to init from canvas size
    protected size: vec2 = vec2.fromValues(0, 0);

    private canvas: Canvas;
    private lights: Light[];
    private _indexInViewMode: number = 0;
    private userInputObservers: any[] = [];  // these are user input event observers for current view
    private viewChangedObservers: any[] = [];
    private viewResizedObservers: any[] = [];
    private _hud: Hud;
    private _pgc: any = {};

    constructor(aViewMode: ViewMode | ViewModeWebVR, aDocument: RenderDocument, aPositionPct: vec2, aSizePct: vec2) {
        this.document = aDocument;
        this._viewMode = aViewMode;
        this.canvas = this._viewMode.getCanvas();

        this.viewPositionPct = aPositionPct;
        this.viewSizePct = aSizePct;

        // try to set position and size based on current canvas size (for cases that the view is crated on the fly during the application lifetime)
        this._setPositionAndSize(this.canvas.getContentSize());

        this._hud = new Hud(this.document.getSceneAPI(), this);

        this._pgc = {
            sizeWithoutDPR: vec2.create(),
            point3d: vec3.create(),
            point4d: vec4.create()
        };

        this._controls = null;
    }

    public getViewMode(): ViewMode | ViewModeWebVR {
        return this._viewMode;
    }

    public getSize(): vec2 {
        return this.size;
    }

    public getSizePct(): vec2 {
        return this.viewSizePct;
    }

    public getPosition(): vec2 {
        return this.position;
    }

    public getControls(): ViewControls {
        return this._controls;
    }

    public getDocument(): RenderDocument {
        return this.document;
    }

    /**
     * View size / quality performance factor (device pixel ratio)
     *
     * @return {vec2}
     */
    public getSizeWithoutDPR(): vec2 {
        vec2.copy(this._pgc.sizeWithoutDPR, this.size);
        let factor = this.document.getSceneAPI().renderSettingsModule.getQualityPerformanceFactor();
        this._pgc.sizeWithoutDPR[0] /= factor;
        this._pgc.sizeWithoutDPR[1] /= factor;

        return this._pgc.sizeWithoutDPR;
    }

    public setIndexInViewMode(aValue: number) {
        this._indexInViewMode = aValue;
    }

    public getIndexInViewMode(): number {
        return this._indexInViewMode;
    }

    public getContentSize(): vec2 {
        return this.canvas.getContentSize();
    }

    public getHTMLCanvas(): HTMLCanvasElement {
        return this.canvas.getHTMLCanvas();
    }

    public getHud(): Hud {
        return this._hud;
    }

    public addLight(aLight: Light): boolean {
        for (let light of this.lights) {
            if (light.getUID() === aLight.getUID()) {
                logInfo("Light with id " + aLight.getUID() + " already exists in view with");
                return false;
            }
        }

        this.lights.push(aLight);

        return true;
    }

    public removeLight(aLight: Light): boolean {
        for (let i = 0; i < this.lights.length; i++) {
            if (this.lights[i].getUID() === aLight.getUID()) {
                this.lights.splice(i, 1);
                return true;
            }
        }

        logInfo("Light with id " + aLight.getUID() + " is not presented in view with");
        return false;
    }

    public clearLights() {
        this.lights = [];
    }

    public getLights(): Light[] {
        return this.lights;
    }

    /**
     * @returns {SunLight}
     */
    public getSunLight(): Light {
        for (let light of this.lights) {
            if (light.getTypeName() === SunLight.TypeName) {
                return light;
            }
        }
    }

    /**
     * @return HeadLight
     */
    public getHeadLight(): HeadLight {
        for (let light of this.lights) {
            if (light.getTypeName() === HeadLight.TypeName) {
                return light;
            }
        }
    }

    /**
     * @return Camera
     */
    public getCamera(): Camera | CameraWebVR | null {
        return this._controls.getCamera();
    }

    public setCameraParams(aPosition: vec3, aTarget: vec3, aFOV: number) {
        let camera = this.getCamera();

        camera.setCameraPositionAndTarget(aPosition, aTarget);
        if (aFOV) { // when the function is called without the third argument
            camera.setFieldOfView(aFOV);
        }

        this._controls.restore();

        this.viewpointChanged();
    }

    public tick() {
        if (this._controls) {
            this._controls.tick();
        }

        this.document.tick(this);
    }

    public isPixelInView(aPixelPosX: number, aPixelPosY: number): boolean {
        let contentSize = this.canvas.getContentSize();
        aPixelPosY = contentSize[1] - 1 - aPixelPosY;
        return (aPixelPosX >= this.position[0] && aPixelPosX < (this.position[0] + this.size[0])) &&
            (aPixelPosY >= this.position[1] && aPixelPosY < (this.position[1] + this.size[1]));
    }

    public getLastMousePosition(): vec2 {
        let mouseState = this._controls.getMouseState();
        return vec2.fromValues(mouseState.lastX, mouseState.lastY);
    }

    public onBoundingBoxUpdated(aBoundingBox: BoundingBox, aBoundingBoxForNPC: BoundingBox) {
        if (!this._controls) {
            // the onBoundingBoxUpdated notify can arrive before the controls member is initialized
            return;
        }

        this._controls.onBoundingBoxUpdated(aBoundingBox, aBoundingBoxForNPC);
    }

    /**
     * This function is called when the position or view direction of camera in this view has changed.
     *
     */
    public viewpointChanged() {
        this._controls.viewpointChanged();

        let renderer = this.document.getRenderer();
        renderer.viewpointChanged();

        // emmit scene rotated event
        Messenger.fireCDWKEvent(
            this.document.getSceneAPI().getTargetTag(),
            "cwx3d_sceneRotated",
            {
                positionRef: this.getCamera().getCameraPosition(),
                elevation: this.getCamera().getXRotationAngle()
            }
        );

        for (let observer of this.viewChangedObservers) {
            observer.viewChanged();
        }

        this._viewMode.scheduleRedraw();
    }

    public render() {
        this.document.render(this);
    }

    public scheduleResize(aContentSize: vec2) {
        this._setPositionAndSize(aContentSize);

        let camera = this.getCamera();
        camera.viewResized(aContentSize[0] / aContentSize[1]);

        this.document.getRenderer().scheduleResize();

        for (let observer of this.viewResizedObservers) {
            observer.viewResized(aContentSize);
        }
    }

    public getAspectRatio(): number {
        if (this.size[1] === 0) {
            return 1.0; // default aspect ratio for the case that the size of the view is zero (or is not yet initialized)
        }
        return this.size[0] / this.size[1];
    }

    public horizontalPixelsToRad(aPixDiff: number): number {
        return (aPixDiff / this.size[0]) * 6.0;
    }

    public radToHorizontalPixels(aRad: number): number {
        return (aRad / 6.0) * this.size[0];
    }

    public verticalPixelsToRad(aPixDiff: number): number {
        return (aPixDiff / this.size[1]) * 3.0;
    }

    public radToVerticalPixels(aRad: number): number {
        return (aRad / 3.0) * this.size[1];
    }

    public registerUserInputObserver(aObserver) {
        this.userInputObservers.push(aObserver);
    }

    public unregisterUserInputObserver(aObserver) {
        for (let i = 0; i < this.userInputObservers.length; i++) {
            if (this.userInputObservers[i] === aObserver) {
                this.userInputObservers.splice(i, 1);
                return;
            }
        }
    }

    public registerViewChangedObserver(aObserver) {
        this.viewChangedObservers.push(aObserver);
    }

    public unregisterViewChangedObserver(aObserver) {
        for (let i = 0; i < this.viewChangedObservers.length; i++) {
            if (this.viewChangedObservers[i] === aObserver) {
                this.viewChangedObservers.splice(i, 1);
                return;
            }
        }
    }

    public registerViewResizedObserver(aObserver) {
        this.viewResizedObservers.push(aObserver);
    }

    public unregisterViewResizedObserver(aObserver) {
        for (let i = 0; i < this.viewResizedObservers.length; i++) {
            if (this.viewResizedObservers[i] === aObserver) {
                this.viewChangedObservers.splice(i, 1);
                return;
            }
        }
    }

    /**
     * Returns moving state of view are
     *
     * @return {Boolean} moving state of view area
     */
    public isMoving(): boolean {
        return this._controls.isMoving();
    }

    public onMousePress(x: number, y: number, buttonState: number) {
        // Test if this mouse input should be handled by other module
        if (this._hud.onMousePress(x, y, buttonState)) {
            return true;
        }

        if (this._controls.onMousePress(x, y, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onMousePress && observer.onMousePress(x, y, buttonState)) {
                break;
            }
        }
    }

    public onMouseRelease(x: number, y: number, buttonState: number, prevButton: number) {
        // Test if this mouse input should be handled by other module
        if (this._hud.onMouseRelease(x, y, buttonState, prevButton)) {
            return true;
        }

        if (this._controls.onMouseRelease(x, y, buttonState, prevButton)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onMouseRelease && observer.onMouseRelease(x, y, buttonState, prevButton)) {
                break;
            }
        }
    }

    public onMove(x: number, y: number, buttonState: number) {
        // Test if this mouse input should be handled by other module
        if (this._hud.onMove(x, y, buttonState)) {
            return true;
        }

        if (this._controls.onMove(x, y, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onMove && observer.onMove(x, y, buttonState)) {
                break;
            }
        }
    }

    public onDrag(x: number, y: number, delta: number, buttonState: number) {
        if (this._hud.onDrag(x, y, delta, buttonState)) {
            return;
        }

        if (this._controls.onDrag(x, y, delta, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onDrag && observer.onDrag(x, y, delta, buttonState)) {
                break;
            }
        }
    }

    public onWheel(x: number, y: number, delta: number, buttonState: number) {
        if (this._controls.onWheel(x, y, delta, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onWheel && observer.onWheel(x, y, buttonState)) {
                break;
            }
        }
    }

    public onMouseIn(x: number, y: number, buttonState: number) {
        if (this._controls.onMouseIn(x, y, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onMouseIn && observer.onMouseIn(x, y, buttonState)) {
                break;
            }
        }
    }

    public onMouseOut(x: number, y, buttonState: number) {
        if (this._controls.onMouseOut(x, y, buttonState)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onMouseOut && observer.onMouseOut(x, y, buttonState)) {
                break;
            }
        }
    }

    public onClick(x: number, y: number, buttonState: number, aDraggedDuringClick: boolean) {
        // Test if this mouse input should be handled by other module
        if (this._hud.onClick(x, y, buttonState, aDraggedDuringClick)) {
            return true;
        }

        if (this._controls.onClick(x, y, buttonState, aDraggedDuringClick)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onClick && observer.onClick(x, y, buttonState, aDraggedDuringClick)) {
                break;
            }
        }
    }

    public onDoubleClick(x: number, y: number) {
        // Test if this mouse input should be handled by other module
        if (this._hud.onDoubleClick(x, y)) {
            return true;
        }

        if (this._controls.onDoubleClick(x, y)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onDoubleClick && observer.onDoubleClick(x, y)) {
                break;
            }
        }
    }

    public onTouchStart(x: number, y: number, numTouches: number) {
        if (this._controls.onTouchStart(x, y, numTouches)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onTouchStart && observer.onTouchStart(x, y, numTouches)) {
                break;
            }
        }
    }

    public onTouchMove(x: number, y: number, numTouches: number, delta: number) {
        if (this._controls.onTouchMove(x, y, numTouches, delta)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onTouchMove && observer.onTouchMove(x, y, numTouches, delta)) {
                break;
            }
        }
    }

    public onTouchEnd(x: number, y: number, remainingTouch: number, aDraggedDuringTouch: boolean) {
        if (this._controls.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onTouchEnd && observer.onTouchEnd(x, y, remainingTouch, aDraggedDuringTouch)) {
                break;
            }
        }
    }

    public onSingleTap(x: number, y: number) {
        if (this._controls.onSingleTap(x, y)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onSingleTap && observer.onSingleTap(x, y)) {
                break;
            }
        }
    }

    public onDoubleTap(x: number, y: number) {
        if (this._controls.onDoubleTap(x, y)) {
            return;
        }

        for (let observer of this.userInputObservers) {
            if (observer.onDoubleTap && observer.onDoubleTap(x, y)) {
                break;
            }
        }
    }

    public onKeyDown(key: string) {
        this._controls.onKeyDown(key);
    }

    public onKeyUp(key: string) {
        this._controls.onKeyUp(key);
    }

    public onKeyPress(key: string) {
        this._controls.onKeyPress(key);
    }

    public onFocus(aEvt: FocusEvent) {
        this._controls.onFocus();
    }

    public getCenterWindowPoint(): vec2 {
        return vec2.fromValues(
            this.position[0] + (this.size[0] / 2.0),
            this.getContentSize()[1] - 1 - (this.position[1] + (this.size[1] / 2.0))
        );
    }

    public projectPointToWindow(aPoint: vec3): vec4 {
        project(
            this._pgc.point4d,
            aPoint,
            this.getCamera().getViewProjectionMatrix(),
            [this.position[0], this.position[1], this.size[0], this.size[1]]
        );

        this._pgc.point4d[1] = this.getContentSize()[1] - 1 - this._pgc.point4d[1];

        return this._pgc.point4d;
    }

    public getDepthValueOfPoint(aPoint: vec3): number {
        project(
            this._pgc.point4d,
            aPoint,
            this.getCamera().getViewProjectionMatrix(),
            [this.position[0], this.position[1], this.size[0], this.size[1]]
        );

        return Math.max(Math.min(this._pgc.point4d[2], 1.0), 0.0);
    }

    public projectWindowPointTo3DPoint(aX: number, aY: number, aDepthValue: number): vec3 {
        // get the picked point position in model space based on the canvas size and viewport defined by the view (the scale plays no role here)
        unProject(
            this._pgc.point3d,
            aX,
            this.getHTMLCanvas().height - 1.0 - aY,
            aDepthValue,
            this.getCamera().getViewProjectionMatrix(),
            [this.position[0], this.position[1], this.size[0], this.size[1]]
        );

        return this._pgc.point3d;
    }

    public setViewControls(aViewControlsType: EViewControls, aSourceViewControls: ViewControls) {
        let needSetStartPosition = false;
        let startPosition = null;
        let startTarget = null;
        let startFOV = null;

        if (aSourceViewControls) {
            // use data from source view controls to set values for the new view controls
            startPosition = vec3.clone(aSourceViewControls.getCamera().getCameraPosition());
            startTarget = vec3.clone(aSourceViewControls.getCamera().getCameraTarget());
            startFOV = aSourceViewControls.getCamera().getFieldOfView();
            needSetStartPosition = true;
            aSourceViewControls.destroy();
        }
        else if (this._controls) {
            startPosition = vec3.clone(this._controls.getCamera().getCameraPosition());
            startTarget = vec3.clone(this._controls.getCamera().getCameraTarget());
            startFOV = this._controls.getCamera().getFieldOfView();
            needSetStartPosition = true;
            this._controls.destroy();
        }

        switch (aViewControlsType) {
            case EViewControls.EXAMINER:
                this._controls = new ExaminerControls(this.document, this);
                break;

            case EViewControls.EXAMINER_STEREO:
                this._controls = new ExaminerControlsStereo(this.document, this);
                break;

            case EViewControls.EXAMINER_WEBVR:
                this._controls = new ExaminerControlsWebVR(this.document, this);
                break;

            case EViewControls.WALK:
                this._controls = new WalkControls(this.document, this);
                break;

            case EViewControls.WALK_STEREO:
                this._controls = new WalkControlsStereo(this.document, this);
                break;

            case EViewControls.WALK_WEBVR:
                this._controls = new WalkControlsWebVR(this.document, this);
                break;

            case EViewControls.FLY:
                this._controls = new FlyControls(this.document, this);
                break;

            case EViewControls.FLY_STEREO:
                this._controls = new FlyControlsStereo(this.document, this);
                break;

            case EViewControls.FLY_WEBVR:
                this._controls = new FlyControlsWebVR(this.document, this);
                break;

            case EViewControls.YANIMATION:
                this._controls = new YAnimationControls(this.document, this);
                break;

            case EViewControls.FILE_ANIMATION:
                this._controls = new FileAnimationControls(this.document, this);
                break;

            case EViewControls.FILE_ANIMATION_STEREO:
                this._controls = new FileAnimationControlsStereo(this.document, this);
                break;

            case EViewControls.FILE_ANIMATION_WEBVR:
                this._controls = new FileAnimationControlsWebVR(this.document, this);
                break;

            default :
                break;
        }

        if (aSourceViewControls) {
            this._controls.setCumulRotation(aSourceViewControls.getCumulRotation());
        }

        if (needSetStartPosition) {
            this.setCameraParams(startPosition, startTarget, startFOV);
        }

        this._controls.init();
    }

    public initLogarithmicDepthBuffer() {
        if (this._controls) {
            this._controls.checkAndInitLogarithmicDepthBuffer();
        }
    }

    public getViewControls(): ViewControls {
        return this._controls;
    }

    /**
     *  Ask for pointer lock for this view.
     */
    public setUsePointerLock() {
        this._controls.setUsePointerLock();
    }

    /**
     * Clone observers from ond view to another (used when changing view mode as the views are recreated)
     *
     * @param oldActiveView active view from vie wmode which will be destroyed
     */
    public inheritObservers(oldActiveView: View) {
        this.userInputObservers = oldActiveView.userInputObservers.slice();
        this.viewChangedObservers = oldActiveView.viewChangedObservers.slice();
        this.viewResizedObservers = oldActiveView.viewResizedObservers.slice();
    }

    protected _setPositionAndSize(aContentSize: vec2) {
        this.position = vec2.fromValues(aContentSize[0] * (this.viewPositionPct[0] / 100), aContentSize[1] * (this.viewPositionPct[1] / 100));
        this.size = vec2.fromValues(aContentSize[0] * (this.viewSizePct[0] / 100), aContentSize[1] * (this.viewSizePct[1] / 100));
    }
}
